import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Shield, Check, Award, FileCheck } from 'lucide-react';
import { cn } from '@/lib/utils';

interface RegulatoryApprovalsCardProps {
  approvals?: string[] | null;
  approvalNumbers?: Record<string, string> | null;
  certifications?: string[] | null;
  className?: string;
}

// Map approval types to display info
const approvalConfig: Record<string, { label: string; color: string }> = {
  'easa_145': { label: 'EASA Part-145', color: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400' },
  'faa_145': { label: 'FAA Part-145', color: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400' },
  'easa_camo': { label: 'EASA CAMO', color: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400' },
  'easa_21j': { label: 'EASA Part-21J (DOA)', color: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400' },
  'easa_21g': { label: 'EASA Part-21G (POA)', color: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400' },
  'faa_repair_station': { label: 'FAA Repair Station', color: 'bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-900/30 dark:text-sky-400' },
  'easa_147': { label: 'EASA Part-147 (Training)', color: 'bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400' },
  'easa_aoc': { label: 'EASA AOC', color: 'bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400' },
  'faa_aoc': { label: 'FAA AOC (Part 135)', color: 'bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-900/30 dark:text-teal-400' },
  'iso_9001': { label: 'ISO 9001', color: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300' },
  'as9100': { label: 'AS9100', color: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300' },
};

const getApprovalDisplay = (approval: string): { label: string; color: string } => {
  const config = approvalConfig[approval.toLowerCase().replace(/[\s-]/g, '_')];
  if (config) return config;
  
  // Default display for unknown approvals
  return {
    label: approval,
    color: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300'
  };
};

export const RegulatoryApprovalsCard: React.FC<RegulatoryApprovalsCardProps> = ({
  approvals,
  approvalNumbers,
  certifications,
  className
}) => {
  const hasApprovals = approvals && approvals.length > 0;
  const hasCertifications = certifications && certifications.length > 0;
  
  if (!hasApprovals && !hasCertifications) return null;

  return (
    <Card className={cn("border-emerald-200/60 dark:border-emerald-700/40 bg-gradient-to-br from-emerald-50/50 to-green-50/30 dark:from-emerald-900/20 dark:to-green-900/10", className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
          <Shield className="w-4 h-4" />
          Regulatory Approvals
          {hasApprovals && (
            <Badge variant="secondary" className="text-[10px] ml-auto bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
              {approvals.length} approval{approvals.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Regulatory Approvals */}
        {hasApprovals && (
          <div className="space-y-2">
            {approvals.map((approval, idx) => {
              const display = getApprovalDisplay(approval);
              const approvalNumber = approvalNumbers?.[approval];
              
              return (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-2.5 bg-white/60 dark:bg-black/20 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                    <Badge variant="outline" className={cn("text-xs", display.color)}>
                      {display.label}
                    </Badge>
                  </div>
                  {approvalNumber && (
                    <span className="text-xs text-muted-foreground font-mono">
                      #{approvalNumber}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Custom Certifications */}
        {hasCertifications && (
          <div className={cn(hasApprovals && "pt-3 border-t border-emerald-200/50 dark:border-emerald-700/30")}>
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Additional Certifications</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {certifications.map((cert, idx) => (
                <Badge key={idx} variant="outline" className="text-[10px] bg-white/60 dark:bg-black/20">
                  <FileCheck className="w-2.5 h-2.5 mr-1" />
                  {cert}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RegulatoryApprovalsCard;
